import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, UserPlus, Upload, RefreshCw, CheckCircle2, AlertTriangle, FileDown, FileSpreadsheet, LayoutGrid, Trash2, List } from "lucide-react";
import BiometricDeviceImport from "./BiometricDeviceImport";
import BiometricAttendanceGrid from "./BiometricAttendanceGrid";
import UnmatchedGrid from "./UnmatchedGrid";

function formatApiErrorDetail(detail) {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
  return JSON.stringify(detail);
}

export default function BiometricMatching() {
  const [tab, setTab] = useState("unmatched"); // 'unmatched' | 'device-import' | 'grid'
  const [unmatchedView, setUnmatchedView] = useState("list"); // 'list' | 'grid'
  const [resetOpen, setResetOpen] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetReason, setResetReason] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [data, setData] = useState({ rows: [], total_unmatched_punch_ids: 0,
    total_unmatched_punches: 0, total_matched_punches: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [assignTarget, setAssignTarget] = useState(null);
  const [empQuery, setEmpQuery] = useState("");
  const [empResults, setEmpResults] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [retroBusy, setRetroBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/biometric/unmatched");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load unmatched punches");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Employee autocomplete inside assign dialog
  useEffect(() => {
    if (!assignTarget) return;
    if (empQuery.trim().length < 2) {
      setEmpResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/employees", { params: { q: empQuery, limit: 20 } });
        const arr = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
        setEmpResults(arr);
      } catch (e) {
        // Fallback search by paging through all employees
        try {
          const all = await api.get("/employees");
          const list = (all.data || []).filter(e =>
            (e.name || "").toLowerCase().includes(empQuery.toLowerCase()) ||
            (e.employee_code || "").toLowerCase().includes(empQuery.toLowerCase())
          ).slice(0, 20);
          setEmpResults(list);
        } catch {
          setEmpResults([]);
        }
      }
    }, 220);
    return () => clearTimeout(t);
  }, [empQuery, assignTarget]);

  const filteredRows = useMemo(() => {
    if (!search) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter(r => r.punch_id.toLowerCase().includes(q));
  }, [data.rows, search]);

  const doAssign = async (emp) => {
    try {
      const r = await api.post("/biometric/assign", {
        punch_id: assignTarget.punch_id,
        employee_id: emp.id,
      });
      toast.success(
        `Mapped ${assignTarget.punch_id} → ${emp.employee_code} ${emp.name}. ` +
        `Retro-updated ${r.data.retro_punches_updated} historical punch(es).`
      );
      setAssignTarget(null);
      setEmpQuery("");
      setEmpResults([]);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Assign failed");
    }
  };

  const doRetroMatch = async () => {
    setRetroBusy(true);
    try {
      const r = await api.post("/biometric/retro-match");
      toast.success(`Retro-match complete — ${r.data.matched} punch(es) attributed`);
      load();
    } catch (e) {
      toast.error("Retro-match failed");
    } finally {
      setRetroBusy(false);
    }
  };

  const doBulkUpload = async () => {
    if (!bulkFile) return toast.error("Choose a CSV / Excel file first");
    const fd = new FormData();
    fd.append("file", bulkFile);
    try {
      const r = await api.post("/biometric/bulk-assign-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkResult(r.data);
      toast.success(
        `Updated ${r.data.employees_updated} employees · ` +
        `Retro-matched ${r.data.retro_punches_matched} punches`
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk upload failed");
    }
  };

  const downloadTemplate = () => {
    const csv = "employee_code,biometric_id\nBPL-66,48\nBPL-70,12\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "biometric_mapping_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const doResetAll = async () => {
    if (resetToken !== "CLEAR") {
      return toast.error('Type CLEAR exactly to confirm');
    }
    setResetBusy(true);
    try {
      const r = await api.post("/biometric/clear-all-mappings",
        { confirm_token: resetToken, reason: resetReason || "Manual reset" });
      toast.success(
        `Cleared ${r.data.employees_cleared} employee mappings · ` +
        `${r.data.punches_unmatched} punches now unmatched. ` +
        `Snapshot saved (rollback possible).`,
        { duration: 8000 }
      );
      setResetOpen(false);
      setResetToken("");
      setResetReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally {
      setResetBusy(false);
    }
  };

  const totalDistinct = data.total_unmatched_punch_ids;
  const totalUnmatchedPunches = data.total_unmatched_punches;
  const totalMatched = data.total_matched_punches;
  const totalAll = totalMatched + totalUnmatchedPunches;
  const pct = totalAll ? Math.round((totalMatched / totalAll) * 100) : 0;

  return (
    <div data-testid="biometric-matching-page" className="p-6 space-y-5 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Admin</div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Biometric Matching
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Map device punch IDs to employees. Numeric IDs are matched after stripping
            leading zeros and whitespace, so <code>96</code> matches <code>00000096</code> automatically.
            Payroll / CSV import path is untouched.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="bio-match-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={doRetroMatch} disabled={retroBusy}
                  data-testid="bio-match-retro-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${retroBusy ? "animate-spin" : ""}`} />
            Retro-match all
          </Button>
          <Button variant="outline" size="sm" onClick={downloadTemplate}
                  data-testid="bio-match-template-btn">
            <FileDown className="h-3.5 w-3.5 mr-1.5" /> Download template
          </Button>
          <Button size="sm" onClick={() => { setBulkResult(null); setBulkFile(null); setBulkOpen(true); }}
                  className="brand-bg brand-bg-hover text-white"
                  data-testid="bio-match-bulk-btn">
            <Upload className="h-3.5 w-3.5 mr-1.5" /> Bulk upload mapping
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setResetToken(""); setResetReason(""); setResetOpen(true); }}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="bio-reset-all-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Reset all mappings
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        <button
          onClick={() => setTab("unmatched")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
            tab === "unmatched"
              ? "border-red-600 text-red-700"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="tab-unmatched">
          <UserPlus className="h-3.5 w-3.5 mr-1.5 inline" /> Unmatched punches
        </button>
        <button
          onClick={() => setTab("device-import")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
            tab === "device-import"
              ? "border-red-600 text-red-700"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="tab-device-import">
          <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5 inline" /> Import from device (CSV)
        </button>
        <button
          onClick={() => setTab("grid")}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
            tab === "grid"
              ? "border-red-600 text-red-700"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
          data-testid="tab-grid">
          <LayoutGrid className="h-3.5 w-3.5 mr-1.5 inline" /> Attendance Grid
        </button>
      </div>

      {tab === "device-import" && (
        <BiometricDeviceImport onComplete={load} />
      )}

      {tab === "grid" && (
        <BiometricAttendanceGrid />
      )}

      {tab === "unmatched" && (
      <>
      {/* Progress + counters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-lg p-4 border bg-emerald-50 border-emerald-200">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700">Matched punches</div>
          <div className="text-3xl font-bold mt-1 text-emerald-700 font-mono"
               data-testid="bio-match-count">{totalMatched.toLocaleString()}</div>
        </div>
        <div className={`rounded-lg p-4 border ${totalUnmatchedPunches ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
          <div className="text-[10px] uppercase tracking-widest text-slate-600">Unmatched punches</div>
          <div className={`text-3xl font-bold mt-1 font-mono ${totalUnmatchedPunches ? "text-red-700" : "text-slate-700"}`}
               data-testid="bio-unmatched-count">{totalUnmatchedPunches.toLocaleString()}</div>
        </div>
        <div className="rounded-lg p-4 border bg-amber-50 border-amber-200">
          <div className="text-[10px] uppercase tracking-widest text-amber-900">Distinct unmatched IDs</div>
          <div className="text-3xl font-bold mt-1 text-amber-900 font-mono"
               data-testid="bio-distinct-count">{totalDistinct}</div>
        </div>
        <div className="rounded-lg p-4 border bg-white">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Match rate</div>
          <div className="text-3xl font-bold mt-1 text-slate-800 font-mono">{pct}%</div>
          <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full brand-bg" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {/* View toggle + Search */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex rounded-md border border-slate-200 overflow-hidden">
          <button
            onClick={() => setUnmatchedView("list")}
            className={`px-3 py-1.5 text-xs flex items-center gap-1 ${
              unmatchedView === "list" ? "bg-red-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="unmatched-view-list">
            <List className="h-3 w-3" /> List
          </button>
          <button
            onClick={() => setUnmatchedView("grid")}
            className={`px-3 py-1.5 text-xs flex items-center gap-1 border-l border-slate-200 ${
              unmatchedView === "grid" ? "bg-red-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="unmatched-view-grid">
            <LayoutGrid className="h-3 w-3" /> Calendar Grid
          </button>
        </div>
        {unmatchedView === "list" && (
          <>
            <Search className="h-4 w-4 text-slate-400" />
            <Input
              placeholder="Filter by punch ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs h-8 text-xs"
              data-testid="bio-match-search"
            />
            <span className="text-xs text-slate-500">
              {filteredRows.length} of {data.rows.length} distinct IDs
            </span>
          </>
        )}
      </div>

      {unmatchedView === "grid" && (
        <UnmatchedGrid onAssign={(row) => {
          setAssignTarget(row);
          setEmpQuery("");
          setEmpResults([]);
        }} />
      )}

      {unmatchedView === "list" && (
      <>
      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Punch ID (raw)</th>
              <th className="text-right px-3 py-2">Count</th>
              <th className="text-left px-3 py-2">Earliest</th>
              <th className="text-left px-3 py-2">Latest</th>
              <th className="text-left px-3 py-2">Device(s)</th>
              <th className="text-left px-3 py-2">Sample timestamps</th>
              <th className="text-center px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-8 text-xs text-slate-400">Loading…</td></tr>
            )}
            {!loading && filteredRows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-xs text-emerald-700">
                <CheckCircle2 className="h-4 w-4 inline mr-1.5" />
                {data.rows.length === 0 ? "No unmatched punches — every punch is attributed to an employee." : "No matches for current filter."}
              </td></tr>
            )}
            {!loading && filteredRows.map(r => (
              <tr key={r.punch_id} className="border-t border-slate-100 hover:bg-slate-50"
                  data-testid={`bio-unmatched-row-${r.punch_id}`}>
                <td className="px-3 py-2 font-mono font-semibold">{r.punch_id}</td>
                <td className="px-3 py-2 text-right font-mono">{r.count}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{(r.earliest || "").slice(0, 16)}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{(r.latest || "").slice(0, 16)}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{(r.device_serials || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-slate-500">
                  {(r.samples || []).slice(0, 3).map(s => (s || "").slice(11, 16)).join(" · ")}
                </td>
                <td className="text-center">
                  <Button size="sm"
                          onClick={() => { setAssignTarget(r); setEmpQuery(""); setEmpResults([]); }}
                          className="brand-bg brand-bg-hover text-white"
                          data-testid={`bio-assign-btn-${r.punch_id}`}>
                    <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>
      )}
      </>
      )}

      {/* Assign dialog */}
      <Dialog open={!!assignTarget} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign punch ID “{assignTarget?.punch_id}” to an employee</DialogTitle>
            <DialogDescription className="text-xs">
              All <b>{assignTarget?.count}</b> historical punch(es) with this device ID will be
              attributed to the selected employee. Numerical leading zeros are ignored on match.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Search employee by name or code</Label>
            <Input
              placeholder="e.g. Pintu or BPL-111"
              value={empQuery}
              onChange={(e) => setEmpQuery(e.target.value)}
              autoFocus
              data-testid="bio-assign-search"
            />
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded">
              {empResults.length === 0 && empQuery.length >= 2 && (
                <div className="text-xs text-slate-400 p-3">No employees match “{empQuery}”.</div>
              )}
              {empResults.length === 0 && empQuery.length < 2 && (
                <div className="text-xs text-slate-400 p-3">Type at least 2 characters…</div>
              )}
              {empResults.map(emp => (
                <button
                  key={emp.id}
                  onClick={() => doAssign(emp)}
                  className="block w-full text-left p-2 hover:bg-red-50 border-b border-slate-100 last:border-0"
                  data-testid={`bio-assign-choice-${emp.id}`}>
                  <div className="text-sm font-medium">{emp.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    {emp.employee_code} · punch_id: <span className="font-mono">{emp.punch_id || "—"}</span>
                    {emp.biometric_id && <span> · biometric_id: <b>{emp.biometric_id}</b></span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignTarget(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk upload dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => !o && (setBulkOpen(false), setBulkResult(null))}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Bulk assign biometric IDs from CSV / Excel</DialogTitle>
            <DialogDescription className="text-xs">
              Required columns: <code>employee_code</code> and <code>biometric_id</code>.
              Only these two fields will be updated — payroll fields untouched.
              <Button variant="link" size="sm" onClick={downloadTemplate}
                       className="px-1 h-auto py-0 text-xs">Download template</Button>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="file" accept=".csv,.xlsx,.xls"
                   onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                   data-testid="bio-bulk-file" />
            {bulkResult && (
              <div className="text-xs space-y-1 bg-slate-50 p-3 rounded border border-slate-200">
                <div className="font-semibold flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" /> Upload complete
                </div>
                <div>Rows in file: <b>{bulkResult.total_rows}</b></div>
                <div>Employees updated: <b className="text-emerald-700">{bulkResult.employees_updated}</b></div>
                <div>Rows skipped: <b>{bulkResult.rows_skipped}</b></div>
                <div>Historical punches now matched: <b className="text-emerald-700">{bulkResult.retro_punches_matched}</b></div>
                <div>Total employees with biometric_id: <b>{bulkResult.total_with_biometric_id}</b></div>
                {bulkResult.errors?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-amber-800 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> {bulkResult.errors.length} row error(s)
                    </summary>
                    <ul className="mt-1 text-[11px] text-slate-700 max-h-32 overflow-y-auto pl-4">
                      {bulkResult.errors.map((e, i) => (
                        <li key={`bulk-err-${e.row || i}-${i}`}>Row {e.row}: {e.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setBulkOpen(false); setBulkResult(null); }}>
              Close
            </Button>
            <Button onClick={doBulkUpload} disabled={!bulkFile}
                    className="brand-bg brand-bg-hover text-white"
                    data-testid="bio-bulk-upload-btn">
              <Upload className="h-3.5 w-3.5 mr-1.5" /> Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset all mappings dialog */}
      <Dialog open={resetOpen} onOpenChange={(o) => !o && setResetOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Reset ALL biometric mappings
            </DialogTitle>
            <DialogDescription className="text-xs">
              This will <b>clear every employee's biometric_id</b> AND mark every
              historical punch as <b>unmatched</b>. A snapshot is saved for rollback.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-800 space-y-1">
            <div>• ~{totalMatched.toLocaleString()} punches will become unmatched</div>
            <div>• Live Attendance will show "Unmatched" for all device users</div>
            <div>• Paytime CSV upload / payroll calculation <b>untouched</b></div>
            <div>• Employees' <code>punch_id</code> and <code>employee_code</code> preserved</div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Reason (optional)</Label>
            <Input value={resetReason} onChange={(e) => setResetReason(e.target.value)}
                   placeholder="e.g. Manual reassign of all mappings"
                   data-testid="bio-reset-reason" />
            <Label className="text-xs">Type <b className="font-mono bg-slate-100 px-1 rounded">CLEAR</b> exactly to enable the button</Label>
            <Input value={resetToken} onChange={(e) => setResetToken(e.target.value)}
                   placeholder="CLEAR" autoFocus
                   className={resetToken === "CLEAR" ? "border-red-400" : ""}
                   data-testid="bio-reset-token" />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button onClick={doResetAll} disabled={resetToken !== "CLEAR" || resetBusy}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="bio-reset-confirm-btn">
              {resetBusy ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />}
              Reset everything
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
